import { useState } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { sidebarMenu } from "../config/sidebarConfig";
import { LogOut } from "lucide-react";

const MainLayout = () => {

  const [collapsed] = useState(true);
  const [hovered, setHovered] = useState(false);

  const expanded = hovered || !collapsed;

  const navigate = useNavigate();
  const location = useLocation();

  const user = JSON.parse(localStorage.getItem("user"));

  // ================= LOGOUT =================
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    navigate("/");
  };

  return (
    <div className="min-h-screen flex bg-slate-50/50 text-slate-900 overflow-hidden font-sans">

      {/* ================= SIDEBAR ================= */}
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`hidden lg:flex bg-white border-r border-slate-200 flex-col sticky top-0 h-screen transition-all duration-300 ${
          expanded ? "w-56" : "w-[72px]"
        }`}
      >

        {/* LOGO */}
        <div
          className="flex items-center justify-center py-4 cursor-pointer"
          onClick={() => navigate("/dashboard")}
        >
          <span className="text-xl font-black tracking-tight text-blue-950">
            {expanded ? "WorkMgmt" : "WM"}
          </span>
        </div>

        {/* ================= MENU ================= */}
        <nav className="flex-1 px-2 overflow-y-auto">

          {sidebarMenu.map((group) => {

            return (
              <div key={group.group} className="mb-3">

                {/* GROUP TITLE */}
                {expanded && (
                  <p className="text-[10px] font-bold text-slate-400 px-3 mt-4 mb-2 uppercase tracking-widest">
                    {group.group}
                  </p>
                )}

                {/* ITEMS */}
                {group.items.map((item) => {

                  const isActive =
                    location.pathname === item.path ||
                    location.pathname.startsWith(item.path + "/");

                  return (
                    <button
                      key={item.name}
                      onClick={() => navigate(item.path)}
                      className={`group relative w-full flex items-center ${
                        expanded
                          ? "gap-3 px-3 justify-start"
                          : "justify-center"
                      } py-2 rounded-xl transition-all duration-200 ${
                        isActive
                          ? "bg-blue-950 text-white shadow"
                          : "text-slate-500 hover:bg-slate-50 hover:text-blue-950"
                      }`}
                    >

                      {/* ICON */}
                      <span
                        className={`text-lg ${
                          isActive ? "text-white" : item.color
                        }`}
                      >
                        {item.icon}
                      </span>

                      {/* LABEL */}
                      {expanded && (
                        <span className="font-bold text-sm truncate">
                          {item.name}
                        </span>
                      )}

                      {/* TOOLTIP */}
                      {!expanded && (
                        <span className="absolute left-full ml-3 px-3 py-1 text-xs bg-slate-900 text-white rounded-md opacity-0 group-hover:opacity-100 whitespace-nowrap z-50">
                          {item.name}
                        </span>
                      )}

                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* ================= FOOTER PROFILE ================= */}
        <div className="mt-auto pt-6 border-t border-slate-100 px-2 pb-4">
          <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-2xl hover:bg-slate-100 transition">

            <div className="w-9 h-9 rounded-full bg-emerald-500" />

            {expanded && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate text-slate-900">
                    {user?.name || "User"}
                  </p>
                </div>

                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition"
                >
                  <LogOut size={18} />
                </button>
              </>
            )}
          </div>
        </div>

      </aside>

      {/* ================= MAIN CONTENT ================= */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-y-auto">

        {/* HEADER */}
        <header className="sticky top-0 z-40 bg-blue-950 border-b border-slate-200 px-6 lg:px-12 py-2 flex items-center justify-between">

          {/* Breadcrumb */}
          <div className="hidden lg:flex items-center gap-2 text-sm">
            <button
              onClick={() => navigate("/dashboard")}
              className="text-slate-300 hover:text-white font-semibold"
            >
              Home
            </button>

            <span className="text-slate-400">›</span>

            <span className="font-bold text-white capitalize">
              {location.pathname.split("/")[1] || "dashboard"}
            </span>
          </div>

          {/* Notifications */}
          <div className="relative">
            <div className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 hover:text-blue-950 cursor-pointer">
              🔔
            </div>
            <span className="absolute top-2 right-2 w-3 h-3 bg-rose-500 rounded-full"></span>
          </div>

        </header>

        {/* PAGE CONTENT */}
        <main className="flex-1 pt-4 px-6 lg:px-10 pb-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>

        {/* FOOTER */}
        <footer className="px-6 lg:px-12 py-6 border-t border-slate-100 text-center text-xs font-bold text-slate-400 uppercase tracking-widest bg-white">
          © Work Management System
        </footer>

      </div>
    </div>
  );
};

export default MainLayout;
